/**
 * The login/oauth endpoints answer failures in three different shapes:
 *  - Most failures (401/409/422): the legacy response adapter's
 *    `{success, error:[...], status:[n], data:{...}}` envelope. `data.code`
 *    carries the frozen `AuthFailureCode` — but ONLY when the failure went
 *    through `AccountEligibilityGuard` (`invalid_credentials`,
 *    `account_not_activated`). A bad/unverified OAuth token or "no account
 *    found for this email" are plain `HttpException`s with NO `data.code` at
 *    all — those must be told apart by HTTP status alone.
 *  - Rate limiting (429, both /login and /oauth): bypasses the adapter
 *    entirely — a flat `{status: 'error', message: '<string>'}`, never a
 *    `data.code`. A 429 must be checked BEFORE looking for a code, or it
 *    would fall through to a generic/wrong bucket.
 *  - Validation (422): the adapter shape, `error` is an array of
 *    `Field "x" error: ...` strings, no `data.code`.
 *
 * `kind` values: 'invalid_credentials' | 'not_activated' | 'rate_limited' |
 * 'no_account' | 'generic'. `not_activated` carries `email` (for the inline
 * "resend confirmation" offer); nothing else carries extra data.
 */
export function parseLoginError(err) {
	const status = err?.response?.status;
	const body = err?.response?.data;

	if (status === 429) {
		return { kind: 'rate_limited', message: body?.message || 'Too many login attempts. Please try again later.' };
	}

	const code = body?.data?.code;

	if (code === 'account_not_activated') {
		return { kind: 'not_activated', email: body.data.email, message: 'This account is not activated yet.' };
	}
	if (code === 'invalid_credentials') {
		return { kind: 'invalid_credentials', message: 'Invalid email or password.' };
	}

	// OAuth's code-less cases: a bad/unverified provider token is 401, "no
	// account found for this email" is 409. Neither carries data.code.
	if (status === 401) {
		return { kind: 'invalid_credentials', message: 'Invalid email or password.' };
	}
	if (status === 409) {
		return { kind: 'no_account', message: 'No account found for this email.' };
	}

	const errors = Array.isArray(body?.error) ? body.error : [];
	return { kind: 'generic', message: errors[0] || 'Something went wrong. Please try again.' };
}
